import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Reorder component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconReorder extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M3.98504 8.54837C4.659 7.38473 5.62198 6.2811 6.76047 5.39207C8.33949 4.15904 10.1946 3.38567 11.9985 3.38538C13.7755 3.39059 15.5074 3.94518 16.9568 4.97316C18.4065 6.00134 19.5028 7.45277 20.0952 9.12845C20.2333 9.51898 20.6618 9.72363 21.0523 9.58556C21.4428 9.44749 21.6475 9.01898 21.5094 8.62846C20.8138 6.661 19.5267 4.95684 17.8246 3.74963C16.1224 2.54242 14.0885 1.89124 12.0017 1.88538H11.9996C9.77712 1.88538 7.60581 2.8288 5.83728 4.20982C4.65869 5.13016 3.63164 6.26568 2.86739 7.49595L2.58228 6.35551C2.48181 5.95366 2.07461 5.70934 1.67277 5.8098C1.27092 5.91026 1.0266 6.31746 1.12706 6.71931L1.90386 9.8265C1.95328 10.0411 2.09618 10.23 2.30397 10.3319C2.35926 10.3591 2.41738 10.3792 2.47694 10.3921C2.57384 10.4131 2.67157 10.4142 2.7652 10.3976C2.78252 10.3945 2.79978 10.3908 2.81696 10.3866L5.93849 9.60617C6.34034 9.50571 6.58466 9.09851 6.4842 8.69666C6.38374 8.29482 5.97654 8.05049 5.57469 8.15096L3.98504 8.54837ZM22.095 14.1716C22.0388 13.9305 21.8646 13.7224 21.6139 13.6339C21.462 13.5803 21.3043 13.5785 21.1602 13.6194L18.061 14.3942C17.6591 14.4947 17.4148 14.9019 17.5152 15.3037C17.6157 15.7056 18.0229 15.9499 18.4248 15.8494L20.064 15.4396C19.3917 16.7011 18.4485 17.8058 17.2954 18.6707C15.7529 19.8275 13.9035 20.5036 11.9789 20.6146C10.2174 20.6107 8.4993 20.0669 7.05621 19.0562C5.60747 18.0416 4.50615 16.6056 3.90185 14.9433C3.76032 14.554 3.33002 14.3532 2.94073 14.4947C2.55144 14.6362 2.35059 15.0665 2.49212 15.4558C3.20165 17.4075 4.49473 19.0935 6.19572 20.2848C7.89672 21.4761 9.92318 22.115 11.9999 22.1146C12.0137 22.1146 12.0275 22.1142 12.0413 22.1135C14.2691 21.9898 16.4104 21.2094 18.1954 19.8707C19.3935 18.9721 20.3951 17.8489 21.1487 16.5711L21.4172 17.6449C21.5176 18.0467 21.9248 18.291 22.3267 18.1906C22.7285 18.0901 22.9728 17.6829 22.8724 17.2811L22.095 14.1716Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-reorder') === undefined) {
  customElements.define('cat-icon-reorder', BlocksIconReorder);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-reorder': BlocksIconReorder;
  }
}
